import React from 'react';
import { Link } from 'react-router-dom';
import { useSeoHead } from '../hooks/useSeoHead';
import { GuideLanguageSwitch } from '../components/svs-guide/GuideLanguageSwitch';
import '../components/svs-guide/svs-guide.css';

// English counterpart of KleinunternehmerregelungGuide.tsx — see that file's
// header comment for the V2/T1-T4 context. Structure mirrors V1
// (VatBasicsGuide.tsx / UmsatzsteuerGrundlagenGuide.tsx) and the SVS guides.
const INDEXABLE = false;

const PATH = '/en/guides/small-business-vat-exemption/';
const CANONICAL = `https://pstax.at${PATH}`;
export const TITLE_SBE_EN = 'Small-Business VAT Exemption in Austria 2026 | PSTax';
export const DESCRIPTION_SBE_EN =
  'The small-business VAT exemption (Kleinunternehmerregelung) in Austria 2026: the threshold, no input tax deduction, and when opting into VAT is worth it. Explained by a tax advisor.';
const H1 = 'The small-business VAT exemption in Austria 2026: is opting out worth it?';

const AUTHOR_PERSON = {
  '@type': 'Person',
  name: 'Pascal Schraml',
  jobTitle: 'Certified tax advisor (Steuerberater)',
  worksFor: { '@type': 'Organization', name: 'PSTax Steuerberatung FlexCo', url: 'https://pstax.at' },
};

export const ARTICLE_JSONLD_SBE_EN = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: H1,
  datePublished: '2026-10-02',
  dateModified: '2026-10-02',
  inLanguage: 'en',
  author: AUTHOR_PERSON,
  reviewedBy: AUTHOR_PERSON,
  publisher: { '@type': 'Organization', name: 'PSTax Steuerberatung FlexCo', url: 'https://pstax.at' },
  about: 'Small-business VAT exemption in Austria',
};

const TOC_ITEMS: { label: string; id: string }[] = [
  { label: 'What the small-business exemption actually means', id: 'meaning' },
  { label: 'The catch: no input tax deduction', id: 'input-tax' },
  { label: 'What actually counts towards the threshold', id: 'threshold' },
  { label: 'What happens if you exceed the threshold', id: 'exceeding' },
  { label: 'What has to be on a small-business invoice', id: 'invoice' },
  { label: 'Can you opt out of the small-business exemption?', id: 'opting-out' },
  { label: 'Does the exemption apply in other EU countries too?', id: 'eu' },
  { label: 'Small-business exemption vs. small-business flat-rate scheme', id: 'flat-rate' },
  { label: 'Mistakes we see most often', id: 'mistakes' },
  { label: 'Frequently asked questions', id: 'faq' },
  { label: 'Official sources', id: 'sources' },
];

// Exported so prerenderRoutes.ts can build the FAQPage JSON-LD from the same
// source instead of duplicating the answers as a second copy of the text.
const plainFaq = (q: string, aPlain: string) => ({ q, aPlain, a: <>{aPlain}</> });

export const FAQS_SBE_EN: { q: string; a: React.ReactNode; aPlain: string }[] = [
  plainFaq(
    'Is the small-business threshold gross or net?',
    "Gross – €55,000 including the VAT you're not charging.",
    // F-V2
  ),
  plainFaq(
    'Do sales to clients abroad count towards the threshold?',
    "B2B services to foreign businesses usually don't, because their place of supply is abroad.",
    // F-V4
  ),
  plainFaq(
    'What do I write on my invoice as a small business?',
    'No VAT shown, but a note such as "VAT exempt under the small-business exemption (Kleinunternehmerregelung)".',
    // F-V5
  ),
  plainFaq(
    'Do I need a VAT ID (UID) as a small business?',
    "Only if you do business with companies in other EU countries – it doesn't change your tax-exempt status.",
    // F-V8
  ),
  plainFaq(
    'How long am I locked in if I opt out?',
    'At least 5 calendar years from the year you opt in.',
    // F-V6
  ),
  plainFaq(
    'Do I have to file a VAT return as a small business?',
    'Usually not, unless you exceptionally owe VAT (e.g. via reverse charge) or have opted into standard VAT treatment.',
    // F-V12
  ),
  plainFaq(
    'What happens if I exceed the threshold by a small amount?',
    'Up to €60,500 (10% tolerance), the exemption still runs until year-end; above that it ends immediately.',
    // F-V3
  ),
];

const ExternalLink: React.FC<{ href: string; children: React.ReactNode; className?: string }> = ({
  href,
  children,
  className = '',
}) => (
  <a href={href} target="_blank" rel="noopener" className={className}>
    {children}
    <span aria-hidden="true"> ↗</span>
    <span className="sr-only">, opens in a new tab</span>
  </a>
);

const Callout: React.FC<{ tone: 'blue' | 'orange'; label: string; children: React.ReactNode }> = ({
  tone,
  label,
  children,
}) => (
  <div
    className={`my-6 rounded-2xl border-l-4 p-5 ${
      tone === 'blue' ? 'bg-pstax-blue/10 border-pstax-blue' : 'bg-pstax-orange/10 border-pstax-orange'
    }`}
  >
    <p className="text-[11px] font-black uppercase tracking-wider mb-2 text-pstax-navy">{label}</p>
    <p className="text-pstax-navy leading-relaxed">{children}</p>
  </div>
);

const SoonBadge: React.FC = () => (
  <span className="inline-flex items-center text-[10px] font-black uppercase tracking-wider text-pstax-gray bg-pstax-border/60 rounded-full px-2 py-0.5 ml-2 align-middle">
    Soon
  </span>
);

export const SmallBusinessVatExemptionGuide: React.FC = () => {
  useSeoHead({
    title: TITLE_SBE_EN,
    description: DESCRIPTION_SBE_EN,
    canonical: CANONICAL,
    indexable: INDEXABLE,
    htmlLang: 'en',
    hreflang: [
      { lang: 'en', href: 'https://pstax.at/en/guides/small-business-vat-exemption/' },
      { lang: 'de', href: 'https://pstax.at/de/guides/kleinunternehmerregelung/' },
      { lang: 'x-default', href: 'https://pstax.at/en/guides/small-business-vat-exemption/' },
    ],
    jsonLd: [
      ARTICLE_JSONLD_SBE_EN,
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS_SBE_EN.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.aPlain },
        })),
      },
    ],
  });

  return (
    <div className="svs-guide pt-28 pb-24">
      <div className="container mx-auto px-6 max-w-6xl">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="text-sm text-pstax-gray mb-6">
          <ol className="flex flex-wrap items-center gap-2">
            <li>
              <Link to="/en/" className="hover:text-pstax-link">
                Home
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li>Knowledge Base</li>
            <li aria-hidden="true">/</li>
            <li className="text-pstax-navy font-semibold" aria-current="page">
              Small-business exemption
            </li>
          </ol>
        </nav>

          <article className="min-w-0">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-pstax-blue mb-4">
              Knowledge Base · VAT
            </p>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-pstax-navy mb-6 leading-tight">
              {H1}
            </h1>
            <p className="text-xl text-pstax-gray leading-relaxed mb-4">
              Up to €55,000 revenue a year, you don't have to charge VAT – sounds like less admin. The catch: you
              also don't get any input tax back. Whether that's worth it for you depends on who your clients are
              and how much you spend.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <picture className="shrink-0">
                <source srcSet="/pascal-schraml.webp" type="image/webp" />
                <img
                  src="/pascal-schraml.jpg"
                  alt=""
                  width={36}
                  height={36}
                  decoding="async"
                  className="w-9 h-9 rounded-full object-cover"
                />
              </picture>
              <p className="text-sm text-pstax-gray">
                As of: <time dateTime="2026-10-01">October 2026</time> · Reviewed by{' '}
                <a href="#author" className="font-semibold text-pstax-navy hover:text-pstax-link">
                  Pascal Schraml
                </a>
                , Austrian certified tax advisor (<span lang="de">Steuerberater</span>), founder of PSTax
              </p>
            </div>
            <GuideLanguageSwitch from={PATH} targetLang="DE" label="Diese Seite auf Deutsch →" />

            {/* At a glance */}
            <div className="mt-10 mb-10 bg-white border border-pstax-border rounded-2xl shadow-sm p-6 sm:p-8">
              <h2 className="text-lg font-display font-bold text-pstax-navy mb-4">At a glance</h2>
              <ul className="space-y-2">
                <li className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm text-pstax-navy">
                  <span>
                    Small-business threshold: €55,000 gross/year, unchanged for 2025 and 2026{/* F-V2 */}
                  </span>
                </li>
                <li className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm text-pstax-navy">
                  <span>
                    Tolerance up to €60,500: the exemption continues until year-end, then ends immediately{/* F-V3 */}
                  </span>
                </li>
                <li className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm text-pstax-navy">
                  <span>
                    Only revenue taxable in Austria counts towards the threshold – B2B services to foreign
                    businesses usually don't{/* F-V4 */}
                  </span>
                </li>
                <li className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm text-pstax-navy">
                  <span>
                    As a small business: no input tax, but also no VAT on your invoices{/* F-V5 */}
                  </span>
                </li>
                <li className="flex justify-between gap-4 py-1.5 text-sm text-pstax-navy">
                  <span>
                    Voluntarily opting into VAT (<span lang="de">Regelbesteuerung</span>) locks you in for at
                    least 5 years{/* F-V6 */}
                  </span>
                </li>
              </ul>
            </div>

            <p className="mb-10 leading-relaxed text-pstax-gray">
              Still unclear on the VAT basics? →{' '}
              <Link
                to="/en/guides/vat-basics-austria/"
                className="text-pstax-link hover:underline font-semibold"
              >
                VAT basics in Austria (V1)
              </Link>
            </p>

            {/* Table of contents */}
            <nav className="toc-top mb-12" aria-label="On this page">
              <p className="text-[11px] font-black uppercase tracking-wider text-pstax-gray mb-3">On this page</p>
              <ol className="grid min-[620px]:grid-cols-2 gap-x-8 gap-y-1">
                {TOC_ITEMS.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="flex min-h-[32px] items-center text-sm text-pstax-navy hover:text-pstax-link py-1"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {/* Meaning */}
            <h2 id="meaning" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              What the small-business exemption actually means
            </h2>
            <p className="mb-8 leading-relaxed">
              Stay under €55,000 annual revenue and you don't charge VAT – your invoices carry a fixed note
              instead of a VAT amount{/* F-V2, F-V5 */}. Less admin, but fewer rights too – see the next point.
            </p>

            {/* Input tax */}
            <h2 id="input-tax" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              The catch: no input tax deduction
            </h2>
            <p className="mb-8 leading-relaxed">
              Without VAT on your invoices, you also don't get input tax back on your own purchases{/* F-V5 */}.
              With low expenses that barely matters. With bigger purchases – new equipment, software
              subscriptions, office setup – it costs you real money.
            </p>

            {/* Threshold */}
            <h2 id="threshold" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              What actually counts towards the small-business threshold
            </h2>
            <p className="mb-4 leading-relaxed">
              Only revenue taxable in Austria counts towards the threshold{/* F-V4 */}. B2B services to
              businesses abroad – EU or otherwise – usually have their place of supply abroad and so{' '}
              <strong>don't</strong> count.
            </p>
            <Callout tone="orange" label="Sounds simple, but">
              That means you can earn a lot from clients abroad and still stay a small business in Austria –
              though you'll often still need a VAT ID (UID) and have to file a recapitulative statement
              (ZM){/* F-V8 */}.
            </Callout>

            {/* Exceeding */}
            <h2 id="exceeding" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              What happens if you exceed the threshold
            </h2>
            <p className="mb-4 leading-relaxed">
              Up to €60,500 (10% tolerance), the exemption still runs until year-end. Above that it ends{' '}
              <strong>immediately</strong> – from the exact invoice that crosses the line{/* F-V3 */}. From
              that point you have to charge VAT.
            </p>
            <Callout tone="orange" label="Sounds simple, but">
              If you notice too late, you owe VAT you never actually charged – adding it retroactively with the
              client is often not realistic in practice.
            </Callout>

            {/* Invoice */}
            <h2 id="invoice" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              What has to be on a small-business invoice
            </h2>
            <p className="mb-8 leading-relaxed">
              No VAT shown, but a note such as "VAT exempt under the small-business exemption
              (Kleinunternehmerregelung)"{/* F-V5 */}. If VAT is shown anyway, you owe it to the tax office –
              regardless of whether you were actually exempt.
            </p>

            {/* Opting out */}
            <h2 id="opting-out" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Can you opt out of the small-business exemption?
            </h2>
            <p className="mb-6 leading-relaxed">
              Yes, with an application to opt into standard VAT treatment (<span lang="de">Regelbesteuerungsantrag</span>,
              form U12). That can be worth it if most of your clients are businesses that reclaim VAT anyway, or
              if you're planning bigger investments. Sounds simple, but: opting in locks you in for at least{' '}
              <strong>5 calendar years</strong>{/* F-V6 */} – not a decision to make on a whim.
            </p>

            <div className="overflow-x-auto mb-10">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b-2 border-pstax-navy">
                    <th scope="col" className="text-left py-2 pr-4 font-bold text-pstax-navy">
                      Small business (no VAT) fits when …
                    </th>
                    <th scope="col" className="text-left py-2 font-bold text-pstax-navy">
                      Standard VAT treatment fits when …
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-pstax-border align-top">
                    <td className="py-2 pr-4">your clients are mostly private individuals (they can't reclaim VAT)</td>
                    <td className="py-2">your clients are mostly businesses (VAT costs them nothing)</td>
                  </tr>
                  <tr className="border-b border-pstax-border align-top">
                    <td className="py-2 pr-4">you invest or buy little</td>
                    <td className="py-2">you're planning bigger purchases (equipment, a car, software)</td>
                  </tr>
                  <tr className="align-top">
                    <td className="py-2 pr-4">you want the least possible admin</td>
                    <td className="py-2">you work a lot with foreign B2B clients and want input tax back</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* EU */}
            <h2 id="eu" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Does the exemption apply in other EU countries too?
            </h2>
            <p className="mb-8 leading-relaxed">
              Since 2025, you can use the Austrian small-business exemption for revenue in other EU countries
              too, under certain conditions, as long as your EU-wide annual revenue doesn't exceed
              €100,000 – this needs a separate registration in Austria{/* F-V13 */}.
            </p>

            {/* Flat-rate */}
            <h2 id="flat-rate" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              The small-business VAT exemption is not the same as the small-business flat-rate scheme
            </h2>
            <p className="mb-8 leading-relaxed">
              A common mix-up: the small-business exemption (<span lang="de">Kleinunternehmerregelung</span>) is
              about VAT – whether you charge it. The small-business flat-rate scheme (
              <span lang="de">Kleinunternehmerpauschalierung</span>) is a completely separate income-tax rule
              about how your profit is calculated – more in the profit calculation guide
              <SoonBadge />. One doesn't tell you anything about the other.
            </p>

            {/* Mistakes */}
            <h2 id="mistakes" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Mistakes we see most often
            </h2>
            <ul className="mb-4 space-y-3 list-disc pl-6 leading-relaxed">
              <li>
                Wrongly counting EU B2B revenue towards the €55,000 threshold – and switching to VAT
                unnecessarily as a result
              </li>
              <li>Accidentally issuing an invoice with VAT while still a small business</li>
              <li>Exceeding the threshold but continuing to invoice without VAT for months</li>
              <li>Confusing the small-business VAT exemption with the small-business flat-rate income tax scheme</li>
              <li>Opting out of the exemption without pricing in the 5-year lock-in</li>
            </ul>

            <p className="mb-10 leading-relaxed">
              <strong>What does this mean for me?</strong> Whether the small-business exemption or opting into
              VAT fits you depends on your clients and expenses – we work it out for you in a free 30-minute
              first call.{' '}
              {/* TODO: PublicOnly/InternalOnly once T1 ships.
                  InternalOnly: "Questions about this? Message us in the portal or at clients@pstax.at" */}
              <Link to="/en/#fit-check" className="text-pstax-link hover:underline font-semibold">
                Book a free first call
              </Link>
              .
            </p>

            {/* FAQ */}
            <h2 id="faq" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Frequently asked questions
            </h2>
            <div className="space-y-3 mb-10">
              {FAQS_SBE_EN.map((faq) => (
                <details key={faq.q} className="group bg-white border border-pstax-border rounded-2xl overflow-hidden">
                  <summary className="cursor-pointer list-none px-5 py-4 min-h-[44px] flex items-center justify-between gap-4 font-semibold text-pstax-navy">
                    <span>{faq.q}</span>
                    <span aria-hidden="true" className="shrink-0 text-pstax-blue transition-transform group-open:rotate-180">
                      ▾
                    </span>
                  </summary>
                  <div className="px-5 pb-5 text-pstax-gray leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>

            <p className="mb-10 leading-relaxed text-sm text-pstax-gray">
              <strong className="text-pstax-navy">Keep reading</strong> 2-minute quick guide on this topic
              <SoonBadge /> ·{' '}
              <Link to="/en/guides/vat-basics-austria/" className="text-pstax-link hover:underline">
                VAT basics in Austria (V1)
              </Link>{' '}
              · VAT ID number
              <SoonBadge /> · Flat-rate vs. actual expenses
              <SoonBadge />
            </p>

            {/* Sources */}
            <h2 id="sources" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Official sources
            </h2>
            <ul className="mb-4 space-y-4 leading-relaxed">
              <li>
                usp.gv.at / wko.at:{' '}
                <ExternalLink
                  href="https://www.usp.gv.at/themen/steuern-finanzen/umsatzsteuer-ueberblick/weitere-informationen-zur-umsatzsteuer/weitere-steuertatbestaende-und-befreiungen/kleinunternehmen.html"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  Small-business exemption (<span lang="de">Kleinunternehmerregelung</span>)
                </ExternalLink>
                {/* F-V2, F-V3, F-V4, F-V5, F-V6 */}
              </li>
              <li>
                wko.at:{' '}
                <ExternalLink
                  href="https://www.wko.at/steuern/kleinunternehmerregelung-oesterreich-faq"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  Small-business exemption FAQ
                </ExternalLink>
                {/* F-V5 */}
              </li>
              <li>
                wko.at:{' '}
                <ExternalLink
                  href="https://www.wko.at/steuern/unternehmensgruenderinnen-uid-nummern-faq"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  VAT ID numbers FAQ
                </ExternalLink>
                {/* F-V8 */}
              </li>
              <li>
                RIS:{' '}
                <ExternalLink
                  href="https://www.ris.bka.gv.at/NormDokument.wxe?Abfrage=Bundesnormen&Gesetzesnummer=10004873&Paragraf=6"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  § 6 UStG
                </ExternalLink>
                {/* F-V2, F-V13 */}
              </li>
            </ul>

            {/* CTA */}
            <div className="mt-14 bg-pstax-navy text-white rounded-3xl p-8 sm:p-10">
              <h2 className="text-2xl sm:text-3xl font-display font-bold mb-3">
                Not sure whether opting out is worth it?
              </h2>
              <p className="text-white/75 leading-relaxed mb-6 max-w-2xl">
                Whether the small-business exemption or standard VAT treatment fits you depends on your clients
                and expenses – and on a 5-year lock-in if you change your mind. In a free 30-minute first call,
                we work it out for you.{' '}
                {/* TODO: PublicOnly/InternalOnly once T1 ships.
                    InternalOnly: "Already a client? We answer questions like this in the portal or at
                    clients@pstax.at." */}
              </p>
              <div className="flex flex-wrap gap-4 mb-6">
                <Link to="/en/#fit-check" className="btn-accent">
                  Book your free call
                </Link>
              </div>
              <p className="text-xs text-white/50">
                PSTax advises 600+ clients in Austria, fixed prices from €800/year, in German and
                English.
              </p>
            </div>

            {/* Author box */}
            <div id="author" className="mt-10 bg-white border border-pstax-border rounded-2xl p-6 sm:p-8 flex gap-5 items-start">
              <picture className="shrink-0">
                <source srcSet="/pascal-schraml.webp" type="image/webp" />
                <img
                  src="/pascal-schraml.jpg"
                  alt="Pascal Schraml, Austrian certified tax advisor and founder of PSTax."
                  width={76}
                  height={76}
                  loading="lazy"
                  decoding="async"
                  className="w-[76px] h-[76px] rounded-full object-cover"
                />
              </picture>
              <div>
              <p className="font-display font-bold text-pstax-navy text-lg mb-0.5">Pascal Schraml</p>
              <p className="text-sm text-pstax-gray mb-3">
                Austrian certified tax advisor (<span lang="de">Steuerberater</span>), founder of PSTax
              </p>
              <p className="text-sm text-pstax-gray leading-relaxed">
                Advising self-employed people and freelancers in Austria since 2023, bilingual service in German
                and English, 600+ clients.
              </p>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mt-8 p-6 bg-pstax-bg rounded-2xl border border-pstax-border">
              <p className="text-xs text-pstax-gray leading-relaxed">
                General information, not individual tax advice. All figures apply to 2026 and change annually.
                PSTax (Pascal Schraml Steuerberatung FlexCo) is a member of the Austrian Chamber of Tax Advisors
                and Auditors (KSW).
              </p>
            </div>
          </article>
      </div>
    </div>
  );
};
